/**
 * Billing Reducer
 * NgRx reducer for billing state management
 */
import { createReducer, on } from '@ngrx/store';
import { Subscription, UsageResponse, PricingPlan } from '../models/billing.models';
import * as BillingActions from './billing.actions';

export interface BillingState {
  plans: PricingPlan[];
  subscription: Subscription | null;
  usage: UsageResponse | null;
  loading: boolean;
  creating: boolean;
  updating: boolean;
  canceling: boolean;
  error: string | null;
}

export const initialState: BillingState = {
  plans: [],
  subscription: null,
  usage: null,
  loading: false,
  creating: false,
  updating: false,
  canceling: false,
  error: null
};

export const billingReducer = createReducer(
  initialState,

  // Load Plans
  on(BillingActions.loadPlans, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(BillingActions.loadPlansSuccess, (state, { plans }) => ({
    ...state,
    plans,
    loading: false
  })),
  on(BillingActions.loadPlansFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Load Subscription
  on(BillingActions.loadSubscription, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(BillingActions.loadSubscriptionSuccess, (state, { subscription }) => ({
    ...state,
    subscription,
    loading: false
  })),
  on(BillingActions.loadSubscriptionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Load Usage
  on(BillingActions.loadUsage, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(BillingActions.loadUsageSuccess, (state, { usage }) => ({
    ...state,
    usage,
    loading: false
  })),
  on(BillingActions.loadUsageFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Create Subscription
  on(BillingActions.createSubscription, (state) => ({
    ...state,
    creating: true,
    error: null
  })),
  on(BillingActions.createSubscriptionSuccess, (state, { response }) => ({
    ...state,
    subscription: response.subscription,
    creating: false
  })),
  on(BillingActions.createSubscriptionFailure, (state, { error }) => ({
    ...state,
    creating: false,
    error
  })),

  // Update Subscription
  on(BillingActions.updateSubscription, (state) => ({
    ...state,
    updating: true,
    error: null
  })),
  on(BillingActions.updateSubscriptionSuccess, (state, { subscription }) => ({
    ...state,
    subscription,
    updating: false
  })),
  on(BillingActions.updateSubscriptionFailure, (state, { error }) => ({
    ...state,
    updating: false,
    error
  })),

  // Cancel Subscription
  on(BillingActions.cancelSubscription, (state) => ({
    ...state,
    canceling: true,
    error: null
  })),
  on(BillingActions.cancelSubscriptionSuccess, (state, { response }) => ({
    ...state,
    subscription: response.subscription,
    canceling: false
  })),
  on(BillingActions.cancelSubscriptionFailure, (state, { error }) => ({
    ...state,
    canceling: false,
    error
  })),

  // Reactivate Subscription
  on(BillingActions.reactivateSubscription, (state) => ({
    ...state,
    updating: true,
    error: null
  })),
  on(BillingActions.reactivateSubscriptionSuccess, (state, { subscription }) => ({
    ...state,
    subscription,
    updating: false
  })),
  on(BillingActions.reactivateSubscriptionFailure, (state, { error }) => ({
    ...state,
    updating: false,
    error
  })),

  // Redirect to Checkout
  on(BillingActions.redirectToCheckout, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(BillingActions.redirectToCheckoutFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Clear Error
  on(BillingActions.clearError, (state) => ({
    ...state,
    error: null
  }))
);

